/**
 * Platform Capabilities Detection
 *
 * Detects what media and browser capabilities are available on the current platform.
 */

import { PlatformInfo } from './detection';

export interface MediaCapabilities {
  canAutoplay: boolean;
  requiresPlaysinline: boolean;
  supportsGetDisplayMedia: boolean;
  supportsGetUserMedia: boolean;
  maxVideoResolution: { width: number; height: number };
  maxFrameRate: number;
  supportsPictureInPicture: boolean;
  supportsFullscreen: boolean;
}

/**
 * Tests if autoplay is allowed (without user interaction)
 * This is a synchronous heuristic - actual autoplay might still fail
 */
function canAutoplay(platform: PlatformInfo): boolean {
  // iOS never allows autoplay without user interaction
  if (platform.isIOS) {
    return false;
  }

  // Android WebView is restrictive
  if (platform.isAndroid && platform.isWebView) {
    return false;
  }

  // Mobile Chrome allows muted autoplay
  if (platform.isMobile && platform.browser === 'chrome') {
    return true; // With muted=true
  }

  // Desktop browsers generally allow muted autoplay
  if (platform.isDesktop) {
    return true; // With muted=true
  }

  // Conservative default
  return false;
}

/**
 * Determines if playsinline attribute is required
 * iOS requires playsinline to prevent fullscreen takeover
 */
function requiresPlaysinline(platform: PlatformInfo): boolean {
  return platform.isIOS;
}

/**
 * Checks if screen capture is supported
 */
function supportsGetDisplayMedia(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function'
  );
}

/**
 * Checks if camera/microphone access is supported
 */
function supportsGetUserMedia(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

/**
 * Determines maximum recommended video resolution for the platform
 */
function getMaxVideoResolution(platform: PlatformInfo): { width: number; height: number } {
  // Mobile devices - lower resolution to save bandwidth and battery
  if (platform.isMobile || platform.isWebView) {
    // iPhone/Android WebView
    if (platform.isIOS || platform.isAndroid) {
      return { width: 640, height: 480 }; // VGA quality
    }
    // Other mobile
    return { width: 854, height: 480 }; // 480p
  }

  // Desktop - higher resolution
  return { width: 1280, height: 720 }; // 720p HD
}

/**
 * Determines maximum recommended frame rate
 */
function getMaxFrameRate(platform: PlatformInfo): number {
  // WebView and mobile - lower frame rate to save resources
  if (platform.isWebView) {
    return 24; // Cinematic frame rate, easier on battery
  }

  if (platform.isMobile) {
    return 30; // Standard mobile frame rate
  }

  // Desktop - higher frame rate
  return 30; // Standard for video calls
}

/**
 * Checks if Picture-in-Picture API is supported
 */
function supportsPictureInPicture(): boolean {
  return (
    typeof document !== 'undefined' &&
    'pictureInPictureEnabled' in document
  );
}

/**
 * Checks if Fullscreen API is supported
 */
function supportsFullscreen(): boolean {
  return (
    typeof document !== 'undefined' &&
    (typeof document.documentElement.requestFullscreen === 'function' ||
      typeof (document.documentElement as any).webkitRequestFullscreen === 'function' ||
      typeof (document.documentElement as any).mozRequestFullScreen === 'function')
  );
}

/**
 * Gets all media capabilities for the current platform
 */
export function getMediaCapabilities(platform: PlatformInfo): MediaCapabilities {
  return {
    canAutoplay: canAutoplay(platform),
    requiresPlaysinline: requiresPlaysinline(platform),
    supportsGetDisplayMedia: supportsGetDisplayMedia(),
    supportsGetUserMedia: supportsGetUserMedia(),
    maxVideoResolution: getMaxVideoResolution(platform),
    maxFrameRate: getMaxFrameRate(platform),
    supportsPictureInPicture: supportsPictureInPicture(),
    supportsFullscreen: supportsFullscreen(),
  };
}

/**
 * Cached capabilities - call getMediaCapabilities() once per session
 */
let cachedCapabilities: MediaCapabilities | null = null;

/**
 * Gets cached capabilities or detects them
 */
export function getCachedMediaCapabilities(platform: PlatformInfo): MediaCapabilities {
  if (!cachedCapabilities) {
    cachedCapabilities = getMediaCapabilities(platform);
  }
  return cachedCapabilities;
}

/**
 * Clears the cached capabilities (useful for testing)
 */
export function clearCapabilitiesCache(): void {
  cachedCapabilities = null;
}
